// Offline storage utilities for PWA functionality
import { EMERGENCY_CONTACTS, SAFETY_PROTOCOLS } from '../data/emergencyData';

interface OfflineData {
  id: string;
  type: 'post' | 'emergency' | 'resource' | 'protocol';
  data: any;
  timestamp: number;
  synced: boolean;
}

interface CacheConfig {
  maxAge: number; // in milliseconds
  maxItems: number;
}

class OfflineStorage {
  private dbName = 'DisasterConnectDB';
  private version = 1;
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('offlineData')) {
          const store = db.createObjectStore('offlineData', { keyPath: 'id' });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('synced', 'synced', { unique: false });
        }

        if (!db.objectStoreNames.contains('cache')) {
          const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
          cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        if (!db.objectStoreNames.contains('emergencyData')) {
          const emergencyStore = db.createObjectStore('emergencyData', { keyPath: 'id' });
          emergencyStore.createIndex('type', 'type', { unique: false });
        }
      };
    });
  }

  async storeOfflineData(data: Omit<OfflineData, 'id' | 'timestamp' | 'synced'>): Promise<string> {
    if (!this.db) await this.init();

    const id = `${data.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const offlineData: OfflineData = {
      id,
      ...data,
      timestamp: Date.now(),
      synced: false
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineData'], 'readwrite');
      const store = transaction.objectStore('offlineData');
      const request = store.add(offlineData);

      request.onsuccess = () => resolve(id);
      request.onerror = () => reject(request.error);
    });
  }

  async getOfflineData(type?: string): Promise<OfflineData[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineData'], 'readonly');
      const store = transaction.objectStore('offlineData');
      
      let request: IDBRequest;
      if (type) {
        const index = store.index('type');
        request = index.getAll(type);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async markAsSynced(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineData'], 'readwrite');
      const store = transaction.objectStore('offlineData');
      const getRequest = store.get(id);

      getRequest.onsuccess = () => {
        const data = getRequest.result;
        if (data) {
          data.synced = true;
          const updateRequest = store.put(data);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve(); // Data doesn't exist, consider it synced
        }
      };

      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async deleteOfflineData(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineData'], 'readwrite');
      const store = transaction.objectStore('offlineData');
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async cacheData(key: string, data: any, config: CacheConfig = { maxAge: 24 * 60 * 60 * 1000, maxItems: 100 }): Promise<void> {
    if (!this.db) await this.init();

    const cacheItem = {
      key,
      data,
      timestamp: Date.now(),
      maxAge: config.maxAge
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      
      // Clean up old cache items first
      this.cleanupCache(store, config.maxItems);
      
      const request = store.put(cacheItem);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedData(key: string): Promise<any | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readonly');
      const store = transaction.objectStore('cache');
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          const now = Date.now();
          if (now - result.timestamp < result.maxAge) {
            resolve(result.data);
          } else {
            // Data is expired, delete it
            this.deleteCachedData(key);
            resolve(null);
          }
        } else {
          resolve(null);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  async deleteCachedData(key: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['cache'], 'readwrite');
      const store = transaction.objectStore('cache');
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private cleanupCache(store: IDBObjectStore, maxItems: number): void {
    const index = store.index('timestamp');
    const request = index.openCursor();
    let count = 0;

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        count++;
        if (count > maxItems) {
          cursor.delete();
        }
        cursor.continue();
      }
    };
  }

  async storeEmergencyData(type: string, data: any): Promise<void> {
    if (!this.db) await this.init();

    const emergencyData = {
      id: `${type}_${Date.now()}`,
      type,
      data,
      timestamp: Date.now()
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['emergencyData'], 'readwrite');
      const store = transaction.objectStore('emergencyData');
      const request = store.put(emergencyData);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getEmergencyData(type?: string): Promise<any[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['emergencyData'], 'readonly');
      const store = transaction.objectStore('emergencyData');
      
      let request: IDBRequest;
      if (type) {
        const index = store.index('type');
        request = index.getAll(type);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => {
        const results = request.result.map((item: any) => item.data);
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearExpiredData(): Promise<void> {
    if (!this.db) await this.init();

    const now = Date.now();
    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction(['offlineData', 'cache'], 'readwrite');
      
      // Clean offline data
      const offlineStore = transaction.objectStore('offlineData');
      const offlineIndex = offlineStore.index('timestamp');
      const offlineRequest = offlineIndex.openCursor();

      offlineRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const data = cursor.value;
          if (data.synced && (now - data.timestamp > maxAge)) {
            cursor.delete();
          }
          cursor.continue();
        }
      };

      // Clean cache data
      const cacheStore = transaction.objectStore('cache');
      const cacheIndex = cacheStore.index('timestamp');
      const cacheRequest = cacheIndex.openCursor();

      cacheRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          const data = cursor.value;
          if (now - data.timestamp > data.maxAge) {
            cursor.delete();
          }
          cursor.continue();
        }
      };

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  async getStorageUsage(): Promise<{ used: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        quota: estimate.quota || 0
      };
    }
    return { used: 0, quota: 0 };
  }
}

// Singleton instance
export const offlineStorage = new OfflineStorage();

// Utility functions
export const syncOfflineData = async (): Promise<void> => {
  const unsyncedData = await offlineStorage.getOfflineData();
  const unsynced = unsyncedData.filter(item => !item.synced);

  for (const item of unsynced) {
    try {
      let endpoint = '';
      switch (item.type) {
        case 'post':
          endpoint = '/api/posts';
          break;
        case 'emergency':
          endpoint = '/api/emergency';
          break;
        case 'resource':
          endpoint = '/api/resources';
          break;
        default:
          continue;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(item.data)
      });

      if (response.ok) {
        await offlineStorage.markAsSynced(item.id);
      }
    } catch (error) {
      console.error('Failed to sync item:', item.id, error);
    }
  }
};

export const preloadEmergencyData = async (): Promise<void> => {
  try {
    console.log('Preloading emergency data from local sources...');
    
    // Store emergency contacts
    await offlineStorage.storeEmergencyData('contacts', EMERGENCY_CONTACTS);
    console.log('Emergency contacts stored successfully');
    
    // Store safety protocols
    await offlineStorage.storeEmergencyData('protocols', SAFETY_PROTOCOLS);
    console.log('Safety protocols stored successfully');
    
    // Create mock emergency resources data
    const emergencyResources = [
      {
        id: 'shelter-1',
        name: 'Emergency Shelter Network',
        type: 'shelter',
        description: 'Network of emergency shelters and safe houses',
        contact: '1-800-SHELTER'
      },
      {
        id: 'medical-1',
        name: 'Emergency Medical Services',
        type: 'medical',
        description: 'Emergency medical response and first aid',
        contact: '911'
      },
      {
        id: 'food-1',
        name: 'Food Distribution Centers',
        type: 'food',
        description: 'Emergency food distribution and supplies',
        contact: '1-800-FOOD-AID'
      }
    ];
    
    await offlineStorage.storeEmergencyData('resources', emergencyResources);
    console.log('Emergency resources stored successfully');
    
  } catch (error) {
    console.error('Failed to preload emergency data:', error);
  }
};

// Initialize offline storage and preload emergency data
export const initializeOfflineStorage = async (): Promise<void> => {
  try {
    await offlineStorage.init();
    await preloadEmergencyData();
    await offlineStorage.clearExpiredData();
    
    console.log('Offline storage initialized successfully');
  } catch (error) {
    console.error('Failed to initialize offline storage:', error);
  }
};